"use client";

import HorizontalDragScroller from "@/components/HorizontalDragScroller";
import { Button } from "@/components/ui/Button";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import smoothScrollToElement from "@/utils/smoothScroll";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import GalleryDetailPopup from "./GalleryDetailPopup";

const DEFAULT_GALLERY_META = {
  hotelName: "Liberty Hotels Lara - Her Şey Dahil",
  ctaLabel: "Rezervasyon Yap",
};

const DEFAULT_GALLERY_TABS = [
  { id: "all", label: "Tümü" },
  { id: "entertainment", label: "Bilardo" },
  { id: "family", label: "Çok alan" },
  { id: "restaurant", label: "Restorantlar" },
  { id: "tennis", label: "Tenis kortu" },
  { id: "spa", label: "Spa seansı" },
  { id: "massage", label: "Masaj" },
  { id: "bathroom", label: "Banyo" },
  { id: "sauna", label: "Sauna" },
];

const DEFAULT_GALLERY_IMAGES = [
  {
    id: "gallery-1",
    src: "/images/dummy/hotel-detail-1.jpg",
    alt: "Tropikal bitkilerle çevrili büyük havuz",
    categoryIds: ["all", "pool", "family"],
  },
  {
    id: "gallery-2",
    src: "/images/dummy/hotel-detail-2.jpg",
    alt: "Otel dış cephesi ve havuz alanı",
    categoryIds: ["all", "pool", "tennis"],
  },
  {
    id: "gallery-3",
    src: "/images/dummy/hotel-detail-3.jpg",
    alt: "Şık tasarımlı otel lobisi",
    categoryIds: ["all", "spa", "massage"],
  },
  {
    id: "gallery-4",
    src: "/images/dummy/hotel-detail-4.jpg",
    alt: "Modern otel banyosu",
    categoryIds: ["all", "bathroom", "spa"],
  },
  {
    id: "gallery-5",
    src: "/images/dummy/hotel-detail-5.jpg",
    alt: "Çocuk etkinliği boyama alanı",
    categoryIds: ["all", "family"],
  },
  {
    id: "gallery-6",
    src: "/images/dummy/hotel-detail-2.jpg",
    alt: "Otel barında hazırlanan tropikal kokteyl",
    categoryIds: ["all", "restaurant"],
  },
  {
    id: "gallery-7",
    src: "/images/dummy/hotel-detail-1.jpg",
    alt: "Otel açık alan yürüyüş yolu",
    categoryIds: ["all", "family"],
  },
  {
    id: "gallery-8",
    src: "/images/dummy/hotel-detail-3.jpg",
    alt: "Aydınlık otel odası",
    categoryIds: ["all", "spa"],
  },
  {
    id: "gallery-9",
    src: "/images/dummy/hotel-detail-4.jpg",
    alt: "Spa alanında dinlenme alanı",
    categoryIds: ["all", "spa", "sauna"],
  },
  {
    id: "gallery-10",
    src: "/images/dummy/hotel-detail-5.jpg",
    alt: "Gurme restoran sunumlu yemek",
    categoryIds: ["all", "restaurant"],
  },
  {
    id: "gallery-11",
    src: "/images/dummy/hotel-detail-2.jpg",
    alt: "Deniz kenarında otel manzarası",
    categoryIds: ["all", "family"],
  },
  {
    id: "gallery-12",
    src: "/images/dummy/hotel-detail-1.jpg",
    alt: "Ahşap teras ve oturma alanı",
    categoryIds: ["all", "family", "entertainment"],
  },
];

const GalleryPopup = ({ isOpen, onClose, gallery = {} }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState("all");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const t = useTranslationDetector("hotel");

  const {
    hotelName: rawHotelName,
    tabs: providedTabs = [],
    images: providedImages = [],
  } = gallery;

  const hotelName =
    rawHotelName ??
    t("galleryDefaultHotelName", {
      defaultValue: DEFAULT_GALLERY_META.hotelName,
    });

  const ctaLabel = t("makeReservation", {
    defaultValue: DEFAULT_GALLERY_META.ctaLabel,
  });

  const defaultTabsLocalized = useMemo(
    () =>
      DEFAULT_GALLERY_TABS.map((tab) => ({
        ...tab,
        label: t(`galleryTab${tab.id}`, { defaultValue: tab.label }),
      })),
    [t],
  );

  const tabs = useMemo(() => {
    return providedTabs.length > 0 ? providedTabs : defaultTabsLocalized;
  }, [providedTabs, defaultTabsLocalized]);

  const defaultImagesLocalized = useMemo(
    () =>
      DEFAULT_GALLERY_IMAGES.map((image) => ({
        ...image,
        alt: { defaultValue: image.alt },
      })),
    [t],
  );

  const images = useMemo(() => {
    if (providedImages.length > 0) {
      return providedImages;
    }
    return defaultImagesLocalized;
  }, [providedImages, defaultImagesLocalized]);

  const tabCounts = useMemo(() => {
    return tabs.reduce((acc, tab) => {
      if (tab.id === "all") {
        acc[tab.id] = images.length;
        return acc;
      }
      acc[tab.id] = images.filter((image) =>
        image.categoryIds?.includes(tab.id),
      ).length;
      return acc;
    }, {});
  }, [tabs, images]);

  const filteredImages = useMemo(() => {
    if (selectedTabId === "all") {
      return images;
    }
    return images.filter((image) => image.categoryIds?.includes(selectedTabId));
  }, [images, selectedTabId]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return undefined;
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [isMounted, isOpen]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose?.();
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === selectedTabId)) {
      setSelectedTabId(tabs[0]?.id ?? "all");
    }
  }, [tabs, selectedTabId]);

  if (!isMounted || !isOpen) {
    return null;
  }

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const handleTabClick = (tabId) => {
    setSelectedTabId(tabId);
  };

  const handleImageClick = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setIsDetailOpen(true);
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
    setSelectedImageIndex(null);
  };

  const handleCtaClick = () => {
    // Popup'ı kapat
    onClose?.();

    // Popup kapandıktan sonra scroll yap (kısa bir gecikme ile)
    setTimeout(() => {
      // HotelRooms component'ine scroll yap
      const roomsElement = document.getElementById("rooms");
      if (roomsElement) {
        smoothScrollToElement(roomsElement, 108, 0);
      }
    }, 100);
  };

  const handleDetailCtaClick = () => {
    // Önce detail popup'ı kapat
    setIsDetailOpen(false);
    setSelectedImageIndex(null);

    // Sonra parent popup'ı da kapat
    onClose?.();

    // Popup kapandıktan sonra scroll yap (kısa bir gecikme ile)
    setTimeout(() => {
      // HotelRooms component'ine scroll yap
      const roomsElement = document.getElementById("rooms");
      if (roomsElement) {
        smoothScrollToElement(roomsElement, 108, 1800);
      }
    }, 100);
  };

  const popupContent = createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-white md:bg-black/70 md:px-4"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      <div className="relative flex h-full min-h-[300px] w-full max-w-6xl flex-col gap-4 overflow-hidden rounded-[10px] bg-white !pb-0 sm:min-h-[420px] md:max-h-[730px] md:min-h-[560px] md:gap-6 md:p-6">
        <button
          type="button"
          aria-label={t("galleryCloseAriaLabel", {
            defaultValue: "Galeriyi kapat",
          })}
          onClick={() => onClose?.()}
          className="text-foreground md:text-foreground-muted absolute top-5 right-4 z-10 flex cursor-pointer items-center justify-center md:top-9 md:right-6"
        >
          <FontAwesomeIcon
            className="h-6! w-6!"
            icon={byPrefixAndName.far["xmark"]}
            fontSize={24}
          />
        </button>

        <div className="flex flex-col md:gap-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex h-[64px] flex-col justify-center text-center md:px-0">
              <h2 className="text-foreground flex justify-center text-lg font-semibold md:text-xl">
                <span className="inline-block max-w-[300px] truncate md:max-w-none md:overflow-visible md:whitespace-normal">
                  {hotelName}
                </span>
              </h2>
            </div>
            <Button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCtaClick();
              }}
              className="bg-primary absolute bottom-[34px] left-1/2 z-10 inline-flex h-[50px] w-[218px] -translate-x-1/2 transform cursor-pointer items-center justify-center rounded-lg !px-0 py-3 text-base font-medium text-white md:static md:h-[40px] md:w-[139px] md:translate-0 md:transform-none md:text-sm!"
            >
              {ctaLabel}
            </Button>
          </div>

          <HorizontalDragScroller className="scrollbar-hide border-border-weak overflow-x-auto border-t px-4 pt-4 md:border-0 md:px-0 md:py-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className={clsx(
                  "text-foreground cursor-pointer rounded-lg border px-[10px] py-2 text-sm font-normal whitespace-nowrap transition",
                  selectedTabId === tab.id
                    ? "border-primary bg-primary-weaker"
                    : "border-border bg-white",
                )}
              >
                {tab.label}
                {typeof tabCounts[tab.id] !== "undefined" && (
                  <span className="text-muted-foreground ml-1 text-sm font-normal">
                    ({tabCounts[tab.id]})
                  </span>
                )}
              </button>
            ))}
          </HorizontalDragScroller>
        </div>

        <div className="scrollbar-hide min-h-[220px] flex-1 overflow-y-auto px-4 sm:min-h-[320px] md:px-0">
          <div className="flex flex-col gap-2 md:gap-4">
            <h3 className="text-foreground text-lg font-semibold md:text-xl">
              {tabs.find((tab) => tab.id === selectedTabId)?.label ??
                t("galleryTaball", { defaultValue: "Tümü" })}
            </h3>

            {filteredImages.length === 0 ? (
              <div className="border-border bg-muted text-muted-foreground flex h-48 items-center justify-center rounded-xl border border-dashed text-center text-sm">
                {t("galleryEmptyState", {
                  defaultValue: "Seçilen kategoriye ait görsel bulunamadı.",
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:pb-6 lg:grid-cols-4">
                {filteredImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="relative w-full cursor-pointer overflow-hidden rounded-lg"
                    style={{ aspectRatio: "281 / 188" }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleImageClick(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleImageClick(index);
                      }
                    }}
                  >
                    <Image
                      src={image.src}
                      alt={image.alt}
                      fill
                      unoptimized
                      sizes="(min-width: 1280px) 18vw, (min-width: 768px) 25vw, 45vw"
                      className="object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      {popupContent}
      <GalleryDetailPopup
        isOpen={isDetailOpen}
        onClose={handleDetailClose}
        onCtaClick={handleDetailCtaClick}
        images={filteredImages}
        initialIndex={selectedImageIndex ?? 0}
        ctaLabel={ctaLabel}
      />
    </>
  );
};

export default GalleryPopup;
export { DEFAULT_GALLERY_IMAGES, DEFAULT_GALLERY_META, DEFAULT_GALLERY_TABS };
